// frontend/src/services/api.js
// Complete API service for DoorReport frontend

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

class DoorReportAPI {
  constructor() {
    this.accessToken = null;
    this.refreshToken = localStorage.getItem('refreshToken');
  }

  // ==================== AUTH METHODS ====================

  /**
   * Get access token, refreshing if needed
   */
  async getAccessToken() {
    if (this.accessToken) {
      // Check if token will expire soon (within 1 minute)
      try {
        const payload = JSON.parse(atob(this.accessToken.split('.')[1]));
        const expiresAt = payload.exp * 1000;
        
        if (Date.now() > expiresAt - 60000) {
          await this.refreshAccessToken();
        }
      } catch (error) {
        await this.refreshAccessToken();
      }
    } else if (this.refreshToken) {
      await this.refreshAccessToken();
    }
    
    return this.accessToken;
  }

  /**
   * Refresh the access token
   */
  async refreshAccessToken() {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: this.refreshToken })
    });

    if (!response.ok) {
      // Token expired - clear everything
      this.logout();
      throw new Error('Session expired - please log in again');
    }

    const data = await response.json();
    this.accessToken = data.accessToken;
  }

  /**
   * Register a new user
   */
  async register(email, password) {
    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Registration failed');
    }

    const data = await response.json();
    this.accessToken = data.accessToken;
    this.refreshToken = data.refreshToken;
    localStorage.setItem('refreshToken', data.refreshToken);
    
    return data.user;
  }

  /**
   * Login user
   */
  async login(email, password) {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    this.accessToken = data.accessToken;
    this.refreshToken = data.refreshToken;
    localStorage.setItem('refreshToken', data.refreshToken);
    
    return data.user;
  }

  /**
   * Logout user
   */
  async logout() {
    if (this.refreshToken) {
      try {
        await this.authenticatedRequest('/api/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refreshToken: this.refreshToken })
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('refreshToken');
  }

  /**
   * Get current user profile
   */
  async getCurrentUser() {
    return this.authenticatedRequest('/api/auth/me');
  }

  /**
   * Check if user is logged in
   */
  isAuthenticated() {
    return !!this.refreshToken;
  }

  // ==================== STATS METHODS ====================

  /**
   * Get platform-wide stats (property + review counts)
   */
  async getStats() {
    const response = await fetch(`${API_BASE_URL}/api/stats`);
    if (!response.ok) throw new Error('Failed to fetch stats');
    return response.json();
  }

  // ==================== PROPERTY METHODS ====================

  /**
   * Search for properties by address
   */
  async searchProperties(searchQuery) {
    const response = await fetch(
      `${API_BASE_URL}/api/properties/search?q=${encodeURIComponent(searchQuery)}`
    );

    if (!response.ok) {
      throw new Error('Search failed');
    }

    return response.json();
  }

  /**
   * Get or create a property by address
   */
  async getOrCreateProperty(address) {
    const response = await fetch(`${API_BASE_URL}/api/properties`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(address)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get property');
    }

    return response.json();
  }

  /**
   * Get property details with reviews
   */
  async getPropertyDetails(propertyId) {
    const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}`);

    if (!response.ok) {
      throw new Error('Property not found');
    }

    return response.json();
  }

  // ==================== REVIEW METHODS ====================

  /**
   * Cache Street View availability for a property
   */
  async updateStreetViewAvailability(propertyId, available) {
    const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/street-view`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ available })
    });

    if (!response.ok) {
      throw new Error('Failed to update street view availability');
    }

    return response.json();
  }

  /**
   * Get reviews for a property
   */
  async getReviews(propertyId, options = {}) {
    const { page = 1, limit = 10, sortBy = 'recent' } = options;
    
    const params = new URLSearchParams({
      propertyId,
      page: page.toString(),
      limit: limit.toString(),
      sortBy
    });

    const token = await this.getAccessToken().catch(() => null);
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

    const response = await fetch(
      `${API_BASE_URL}/api/reviews?${params}`,
      { headers }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch reviews');
    }

    return response.json();
  }

  /**
   * Submit a new review (requires authentication)
   */
  async submitReview(reviewData) {
    return this.authenticatedRequest('/api/reviews', {
      method: 'POST',
      body: JSON.stringify(reviewData)
    });
  }

  /**
   * Vote on review helpfulness
   */
  async voteReviewHelpful(reviewId, isHelpful) {
    return this.authenticatedRequest(`/api/reviews/${reviewId}/helpful`, {
      method: 'POST',
      body: JSON.stringify({ isHelpful })
    });
  }

  /**
   * Flag a review for moderation
   */
  async flagReview(reviewId, flagType, description) {
    return this.authenticatedRequest(`/api/reviews/${reviewId}/flag`, {
      method: 'POST',
      body: JSON.stringify({ flagType, description })
    });
  }

  // ==================== HELPER METHODS ====================

  /**
   * Make an authenticated request
   */
  async authenticatedRequest(endpoint, options = {}) {
    const token = await this.getAccessToken();
    
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  }

  // ==================== ACCOUNT METHODS ====================

  async getAccountSummary() {
    return this.authenticatedRequest('/api/account/summary');
  }

  async recordHistory(propertyId) {
    return this.authenticatedRequest(`/api/account/history/${propertyId}`, { method: 'POST' });
  }

  async checkFavorite(propertyId) {
    return this.authenticatedRequest(`/api/account/favorites/${propertyId}`);
  }

  async addFavorite(propertyId) {
    return this.authenticatedRequest(`/api/account/favorites/${propertyId}`, { method: 'POST' });
  }

  async removeFavorite(propertyId) {
    return this.authenticatedRequest(`/api/account/favorites/${propertyId}`, { method: 'DELETE' });
  }

  /**
   * Parse address string into components
   * "4821 Oak Ridge Dr Jacksonville FL 32210" -> { addressLine1, city, state, zipCode }
   */
  parseAddress(addressString) {
    addressString.trim().split(/\s+/);
    
    // Extract ZIP code (5 digits at end)
    const zipMatch = addressString.match(/\b\d{5}\b/);
    const zipCode = zipMatch ? zipMatch[0] : '';
    
    // Extract state (2 letter code before ZIP)
    const stateMatch = addressString.match(/\b([A-Z]{2})\b/);
    const state = stateMatch ? stateMatch[1] : '';
    
    // Everything else is address and city
    let remaining = addressString
      .replace(zipCode, '')
      .replace(state, '')
      .trim();
    
    // Split into address and city (assume last 1-2 words are city)
    const words = remaining.split(/\s+/);
    const cityWords = words.slice(-2); // Take last 2 words as city
    const addressWords = words.slice(0, -2);
    
    return {
      addressLine1: addressWords.join(' '),
      city: cityWords.join(' '),
      state,
      zipCode
    };
  }
}

// Export singleton instance
const api = new DoorReportAPI();
export default api;
